import {
  format,
  isToday,
  isTomorrow,
  isYesterday,
  isSameDay,
  isSameWeek,
  addDays,
  subDays,
} from "date-fns";
import { de } from "date-fns/locale";

/**
 * Helper to get a relative date prefix (e.g., "Heute", "Morgen", etc.)
 */
const getFriendlyRelativePrefix = (date: Date, now: Date = new Date()): string | null => {
  if (isToday(date)) return "Heute";
  if (isTomorrow(date)) return "Morgen";
  if (isYesterday(date)) return "Gestern";
  if (isSameDay(date, addDays(now, 2))) return "Übermorgen";
  if (isSameDay(date, subDays(now, 2))) return "Vorgestern";
  return null;
};

/**
 * Formats a date string in a human-friendly way (German):
 * - ±2 days: "Vorgestern", "Gestern", "Heute", "Morgen", "Übermorgen" + time
 * - Same week (from today to end of week): weekday name + time
 * - Otherwise: short weekday + DD.MM. + time
 *
 * Times are always shown with "Uhr" suffix, e.g. "12:30 Uhr"
 */
export const formatFriendlyDate = (dateString: string): string => {
  if (!dateString) return "";

  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  const now = new Date();
  const timeStr = `${format(date, "HH:mm", { locale: de })} Uhr`;

  const prefix = getFriendlyRelativePrefix(date, now);
  if (prefix) return `${prefix} ${timeStr}`;

  // Same week (Monday to Sunday) and in the future
  if (isSameWeek(date, now, { weekStartsOn: 1 }) && date >= now) {
    return `${format(date, "EEEE", { locale: de })} ${timeStr}`;
  }

  // Fallback: short weekday + DD.MM. + time
  const dayStr = format(date, "EE dd. MMM", { locale: de });
  return `${dayStr} ${timeStr}`;
};

/**
 * Formats a birthday date in a human-friendly way (German):
 * - ±2 days: "Vorgestern", "Gestern", "Heute", "Morgen", "Übermorgen" + (age Jahre)
 * - Same week: weekday name + (age Jahre)
 * - Otherwise: DD.MM. + (age Jahre)
 */
export const formatFriendlyBirthday = (birthDateString: string): string => {
  if (!birthDateString) return "";

  // Robustly parse birth date as a calendar date (local)
  // This handles both ISO strings (YYYY-MM-DD) and full timestamps
  let birthDate: Date;
  const isoMatch = birthDateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    const [, y, m, d] = isoMatch.map(Number);
    birthDate = new Date(y, m - 1, d);
  } else {
    birthDate = new Date(birthDateString);
  }

  if (isNaN(birthDate.getTime())) return birthDateString;

  const now = new Date();
  const currentYear = now.getFullYear();

  const birthYear = birthDate.getFullYear();
  const birthMonth = birthDate.getMonth();
  const birthDay = birthDate.getDate();

  // We check three possible years: last, current, and next
  // to find which birthday is "active" (e.g., ±2 days or next upcoming)
  const birthdays = [
    new Date(currentYear - 1, birthMonth, birthDay),
    new Date(currentYear, birthMonth, birthDay),
    new Date(currentYear + 1, birthMonth, birthDay),
  ];

  // 1. Check if any of these is a "friendly" relative date (±2 days)
  let targetBirthday = birthdays[1]; // Default to this year
  let foundFriendly = false;

  for (const b of birthdays) {
    if (getFriendlyRelativePrefix(b, now)) {
      targetBirthday = b;
      foundFriendly = true;
      break;
    }
  }

  // 2. If no friendly date found, use the next upcoming birthday
  if (!foundFriendly) {
    // If this year's birthday is in the past, use next year's
    if (birthdays[1] < now) {
      targetBirthday = birthdays[2];
    } else {
      targetBirthday = birthdays[1];
    }
  }

  // Calculate the age at the target birthday
  const newAge = targetBirthday.getFullYear() - birthYear;
  const ageSuffix = `(${newAge} Jahre)`;

  const prefix = getFriendlyRelativePrefix(targetBirthday, now);
  if (prefix) return `${prefix} ${ageSuffix}`;

  // Same week and in the future
  if (isSameWeek(targetBirthday, now, { weekStartsOn: 1 }) && targetBirthday >= now) {
    return `${format(targetBirthday, "EEEE", { locale: de })} ${ageSuffix}`;
  }

  // Otherwise: DD.MM. + age
  return `${format(targetBirthday, "dd. MMMM", { locale: de })} ${ageSuffix}`;
};

export const formatDate = (dateString: string) => {
  if (!dateString) return "Kein Datum";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return `${format(date, "dd.MM.yyyy HH:mm", { locale: de })} Uhr`;
};

export const formatDateWithoutTime = (dateString: string) => {
  if (!dateString) return "Kein Datum";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return format(date, "dd.MM.yyyy", { locale: de });
};

export const formatDateWithoutTimeAndYear = (dateString: string) => {
  if (!dateString) return "Kein Datum";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return dateString;

  return format(date, "dd.MM.", { locale: de });
};


export const formatDateForForm = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return format(date, "yyyy-MM-dd");
};

export const formatTimeForForm = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";
  return format(date, "HH:mm");
};

export const formatDateTimeForForm = (dateString: string) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return "";

  return format(date, "yyyy-MM-dd'T'HH:mm");
};
